import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  IDashboardTopDemandSourcesParams,
  ITopDemandSourcesInfo,
  queryDashboardTopDemandSources,
} from '~/src/dash/api/dashboards/home';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  IListViewTableColumnInfo,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
  smallTableRowsPerPageOptions,
} from '~/src/dash/components/tables/ListViewTable/common';
import { Row, Rows, Text, usePublicaTranslation } from '~/src/dash/publicaUI';
import { ISessionState } from '~/src/dash/reducers/session-reducers';
import { IUserState } from '~/src/dash/reducers/user-reducers';
import { getHeaderBidderAdaptersForPublisher } from '~/src/dash/utils/header-bidder-adapters';
import {
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { IDashboardSubTable } from '~/src/dash/views/dashboards/DashboardHome/configs/dashboardOverviewConfig';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

const formatTopDemandSourcesResponseIntoLVTRows = (
  demandSources: ITopDemandSourcesInfo[],
  cfg: IDashboardSubTable,
  session: ISessionState,
  user: IUserState,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  const dsMap: Record<string, ITopDemandSourcesInfo> = {};
  demandSources.forEach((ds) => {
    dsMap[ds.BidderType] = ds;
  });
  const demandSourceAdapters = [
    ...getHeaderBidderAdaptersForPublisher(
      '',
      false,
      session.publisher,
      hasRoleSuperUser(user.user),
      true,
    ),
  ];
  demandSourceAdapters.forEach((adapter) => {
    if (dsMap[adapter.key]) {
      dsMap[adapter.key].BidderType = adapter.text;
    }
  });
  demandSources.forEach((demandSource) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(cfg.columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: any = demandSource[responseKey as keyof ITopDemandSourcesInfo];
      if (isNumeric) {
        value = value !== null ? Number(value) : null;
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

interface ITopDemandSourcesTableProps {
  params: IDashboardTopDemandSourcesParams;
  columns: IListViewTableColumns;
  activeColumnKeys: string[];
  rowsPerPage: RowsPerPage;
  setRowsPerPage: React.Dispatch<React.SetStateAction<-1 | 5 | 10 | 25 | 50 | 100>>;
  initialLoadStart: number;
}

export const TopDemandSourcesTable = ({
  params,
  columns,
  activeColumnKeys,
  rowsPerPage,
  setRowsPerPage,
  initialLoadStart,
}: ITopDemandSourcesTableProps) => {
  const { session, user } = useBaseStore();
  const { t } = usePublicaTranslation();
  const isInitialized = useRef(false);

  const [loadState, setLoadState] = useState(LoadingStateLoading);
  const [query, setQuery] = useState({} as IDashboardTopDemandSourcesParams);
  const [responseData, setResponseData] = useState([] as ITopDemandSourcesInfo[]);
  const [order, setOrder] = useState<Order>('desc');
  const [orderBy, setOrderBy] = useState('revenue');
  const [page, setPage] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState(0);

  const readAuthorization = IS_AUTHORIZED(['read_bidder'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_bidder'], user.user);
  const isSuperUser = hasRoleSuperUser(user.user);

  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);

  useEffect(() => {
    if (!isEmpty(params)) {
      unstable_batchedUpdates(() => {
        setQuery(params);
        setPage(0);
      });
    }
  }, [params]);

  useEffect(() => {
    if (!isEmpty(query)) {
      getData();
    }
  }, [query, order, orderBy, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [rowsPerPage]);

  const getData = () => {
    setLoadState(LoadingStateLoading);

    // update query object
    query.statFilter = orderBy;
    query.statOrder = order;
    query.limit = rowsPerPage;
    query.offset = page * rowsPerPage;

    queryDashboardTopDemandSources(session.publisher.ID, query)
      .then((result) => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateHasData);
          setTotalRowCount(result.TotalRowCount);
          setResponseData(result.Data || []);
        });
        if (!isInitialized.current) {
          track(
            'Dash Overview - Initial Top Demand Sources Table Load Time',
            { isSuperUser, initialLoadTime: moment().unix() - initialLoadStart },
            user.user,
          );
          isInitialized.current = true;
        }
      })
      .catch(() => {
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setResponseData([]);
          setSnackbarError(true);
          setSnackbarMessage(t('BiddersSnackbarErrorRetrievingTopDemandSources'));
        });
      });
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo) => {
    const { orderByKey, requestKey, defaultOrderDirection } = column;
    const key = orderByKey || requestKey;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
    });
  };

  const handleSnackbarClose = () => {
    setSnackbarMessage('');
  };

  const rows = formatTopDemandSourcesResponseIntoLVTRows(responseData, { columns }, session, user);

  return (
    <>
      <Rows>
        <Row noHorizontalPadding smallVerticalPadding>
          <Text bold tKey='TopDemandSources'>
            Top Demand Sources
          </Text>
        </Row>
        <Row noPadding>
          <ListViewTable
            activeColumnKeys={activeColumnKeys}
            columns={columns}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleOnClickColumnHeader={handleOnClickColumnHeader}
            isReadAuthorized={readAuthorization.isAuthorized}
            isSuperUser={isSuperUser}
            isWriteAuthorized={writeAuthorization.isAuthorized}
            loadState={loadState}
            order={order}
            orderBy={orderBy}
            page={page}
            rows={rows}
            rowsPerPage={rowsPerPage}
            rowsPerPageOptions={smallTableRowsPerPageOptions}
            totalRowCount={totalRowCount}
            className={'topdemandsourcesdashboard'}
            disableCurrencyConversion
          />
        </Row>
      </Rows>
      <SnackbarView
        autoHideDuration={12000}
        open={!!snackbarMessage}
        error={!!snackbarError}
        onClose={handleSnackbarClose}
      >
        {snackbarMessage}
      </SnackbarView>
    </>
  );
};
